import { useTranslation } from 'next-i18next';
import { NS } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/redux';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import Icon from '@/components/common/iconedLinks/Icon';
import styles from './NewsletterCardContent.module.scss';
import { subscribeToNewsletter } from '@/redux/epics/newsletter.epics';
import { selectUserEmail } from '@/redux/slices/user.slice';
import { selectStoreId } from '@/redux/slices/root.slice';
import { useRef } from 'react';
import { selectNewsletterSubscriptionRequestStatus } from '@/redux/slices/newsletter.slice';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { URLS } from '@/constants/urls';

export const NewsletterCardContent = () => {
  const { t } = useTranslation(NS.CHECKOUT);
  const dispatch = useAppDispatch();
  const email = useAppSelector(selectUserEmail);
  const storeId = useAppSelector(selectStoreId);
  const subscriptionToNewsletterRequestStatus = useAppSelector(selectNewsletterSubscriptionRequestStatus);
  const isMobile = useScreenSizeContext().isMobile;
  const ref = useRef();

  const createIconWithLabelItems = (labels: string[]) => (
    <div className={styles.items}>
      {labels.map((label) => (
        <div key={label} className={styles.item}>
          <Icon src={URLS.THIN_CHECKMARK_BLACK} alt={t('checkMarkIcon', { ns: NS.ALTER })} />
          <div className={styles.content}>{t(label)}</div>
        </div>
      ))}
    </div>
  );

  const idleOrErroredSpecificCardContent = {
    idle: (
      <>
        <div className={styles.button}>
          <CallToActionContainer
            onClick={() => dispatch(subscribeToNewsletter({ email, storeId }))}
            buttonRef={ref} fullWidth={isMobile}
          >
            {t('newsletter.subscribe')}
          </CallToActionContainer>
          {subscriptionToNewsletterRequestStatus === 'errored' && (
            <span className={styles['error-label']}>{"Une erreur s'est produite, merci de réessayer"}</span>
          )}
        </div>
        <span className={styles['legal-conditions']}>
          {t('newsletter.consent')}
        </span>
      </>
    ),
    errored: (
      <div className={styles['errored-bloc']}>
        <CallToActionContainer
          onClick={() => dispatch(subscribeToNewsletter({ email, storeId }))}
          buttonRef={ref} fullWidth={isMobile}
        >
          {t('newsletter.subscribe')}
        </CallToActionContainer>
        {subscriptionToNewsletterRequestStatus === 'errored' && (
          <span className={styles['error-label']}>{"Une erreur s'est produite, merci de réessayer"}</span>
        )}
        <span className={styles['legal-conditions']}>
          {t('newsletter.consent')}
        </span>
      </div>
    )
  };

  const idleOrErroredCommonCardContent = (type: string) => (
    <section className={styles['card-container']}>
      <section className={styles['left-content']}>
        <div className={styles.title}>{t('newsletter.exclusiveBenefits')}</div>
        {createIconWithLabelItems([
          'newsletter.earlyAccessToSales',
          'newsletter.privateSales',
          'newsletter.personalizedWineAdvice',
          'newsletter.freeNoCommitment',
        ])}
        {idleOrErroredSpecificCardContent[type] || null}
      </section>
      {!isMobile && (
        <section className={styles['right-content']}>
          <img
            className={styles.image}
            src={URLS.NEWSLETTER_PUSH_IMAGE}
            alt=""
            width={305}
            height={281}
          />
        </section>
      )}
    </section>
  );

  const succeededCardContent = (() => (
    <section className={styles['succeeded-request']}>
      <Icon src={URLS.CHECKED_CIRCLE_GREEN} width={40} height={40} alt={t('checkMarkIcon', { ns: NS.ALTER })} />
      <span className={styles.title}>{t('report.titleV2')}</span>
      <p className={styles.paragraph}>{t('newsletter.thanks')}</p>
    </section>
  ))();

  return idleOrErroredSpecificCardContent[subscriptionToNewsletterRequestStatus] ?
    idleOrErroredCommonCardContent(subscriptionToNewsletterRequestStatus) :
    succeededCardContent;
};

export default NewsletterCardContent;
